// apps/web/src/components/creators/CreatorManagement.tsx
// Sample component showing creator management UI

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Creator } from '../../types/creator';
import { apiClient } from '../../lib/apiClient';

interface CreatorManagementProps {
  tenantId?: string;
}

export function CreatorManagement(_props: CreatorManagementProps) {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchCreators = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/creators');
        setCreators(response.data.creators);
      } catch (err) {
        setError('Failed to load creators');
        console.error('Error loading creators:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCreators();
  }, []);

  const handleCreateCreator = async (creatorData: any) => {
    try {
      const response = await apiClient.post('/api/v1/creators', creatorData);
      setCreators([...creators, response.data]);
      setShowForm(false);
    } catch (err) {
      setError('Failed to create creator');
      console.error('Error creating creator:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading creators...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="creator-management">
      <div className="header">
        <h2>Creator Management</h2>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'Add Creator'}
        </button>
      </div>

      {showForm && (
        <CreatorForm 
          onSubmit={handleCreateCreator} 
          onCancel={() => setShowForm(false)} 
        />
      )}

      <div className="creators-grid">
        {creators.map(creator => (
          <CreatorCard key={creator.id} creator={creator} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function CreatorCard({ creator }: { creator: Creator }) {
  return (
    <div className="creator-card">
      <h3>{creator.displayName}</h3>
      <p>Type: {creator.creatorType}</p>
      <p>Status: {creator.status}</p>
      <div className="actions">
        <button className="btn btn-secondary">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function CreatorForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    displayName: '',
    creatorType: 'INDIVIDUAL',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'ACTIVE'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="creator-form">
      <h3>Create New Creator</h3>
      <input
        type="text"
        placeholder="Display Name"
        value={formData.displayName}
        onChange={(e) => setFormData({...formData, displayName: e.target.value})}
        required
      />
      <select
        value={formData.creatorType}
        onChange={(e) => setFormData({...formData, creatorType: e.target.value})}
      >
        <option value="INDIVIDUAL">Individual</option>
        <option value="CREATOR_BUSINESS">Creator Business</option>
        <option value="CREATOR_COLLECTIVE">Creator Collective</option>
        <option value="MEDIA_CREATOR">Media Creator</option>
        <option value="VIRTUAL_CREATOR">Virtual Creator</option>
        <option value="OTHER">Other</option>
      </select>
      <input
        type="email"
        placeholder="Email"
        value={formData.email}
        onChange={(e) => setFormData({...formData, email: e.target.value})}
      />
      <input
        type="tel"
        placeholder="Phone"
        value={formData.phone}
        onChange={(e) => setFormData({...formData, phone: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}